// The chrome: one bar of view tabs across the top, and nothing down the side.
//
// The left rail was a fixed 13.5rem of furniture whose only job was switching between
// seven views — a cost paid on every screen, for a decision made a few times an hour.
// The views are now tabs in the header, which frees the whole window width for the
// sheet and makes the app read as what it is: one table you re-point, not seven pages.
//
// Tabs name the NOUN on the page, not the wrapper: "Deals", not "Pipeline"; "Work",
// not "Projects" (which gets confused with deals in conversation). The active tab is
// underlined in its own accent — ember for money, marine for work — which is the only
// place the hue appears in the chrome.
//
// The row you click doesn't navigate away: it opens the PEEK panel beside the sheet,
// so triage never loses your place in the list. `?peek=<id>` carries it, so the back
// button closes the panel and a reload restores it.

import { Fragment, useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom'

import { api } from '../lib/api'
import { todayIso } from '../lib/format'
import { entitiesOfKind, stageOf } from '../lib/reducer'
import { useStore } from '../lib/store'
import { themeMode, toggleTheme, type Mode } from '../lib/theme'
import { STRIP_CLASS, TAB_CLASS, TABS, hueVar, placeOf, tabStyle, type Tab } from '../lib/views'
import { CommandBar } from './CommandBar'
import { DemoBar, DEMO_CONTACT } from './DemoBar'
import { Icon } from './Icon'
import { Peek } from './Peek'
import { WorkspaceMenu } from './WorkspaceMenu'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'

export function Layout() {
  const { state, config, version, demo } = useStore()
  const [cmdOpen, setCmdOpen] = useState(false)
  const scroller = useRef<HTMLElement | null>(null)
  const { pathname } = useLocation()
  const [params, setParams] = useSearchParams()
  const peekId = params.get('peek')

  // The app owns its own URL now, so a reload lands where you were with no help —
  // the route-memory hook the General Text build needed is gone. What still has to
  // be done by hand: the scroll container here is <main>, which never unmounts
  // across routes, so nothing resets it and a new page would otherwise open
  // wherever the last one was scrolled to.
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 })
    window.scrollTo(0, 0)
  }, [pathname])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCmdOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const closePeek = () => {
    const next = new URLSearchParams(params)
    next.delete('peek')
    setParams(next, { replace: true })
  }

  // A review queue nobody can see is a queue that does not get reviewed, so the
  // pending count is polled rather than waiting for someone to click Staging.
  // Suggestions are not in the log — see worker/log-do.ts — so the store cannot
  // know about them and this is the only way to hear.
  const [pending, setPending] = useState(0)
  useEffect(() => {
    let live = true
    const poll = () =>
      api
        .suggestions('pending')
        .then((r) => live && setPending(r.suggestions.length))
        .catch(() => undefined)
    void poll()
    const t = setInterval(poll, 60_000)
    return () => {
      live = false
      clearInterval(t)
    }
  }, [])

  // Counts that earn their place: what's open, what's live, who we know.
  const counts = (() => {
    void version
    const openDeals = entitiesOfKind(state, 'deal').filter(
      (d) => config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind === 'open',
    ).length
    // Overdue rather than everything dated: a count is only worth a tab if it
    // tells you whether to go there, and "17 things have dates" does not.
    const today = todayIso()
    const overdue =
      Object.values(state.groups).filter((g) => !g.archived && g.due && g.due < today).length +
      Object.values(state.items).filter(
        (i) => !i.archived && i.due && i.due < today && i.status !== 'done',
      ).length

    return {
      '/deals': openDeals,
      '/work': entitiesOfKind(state, 'project').length,
      '/artifacts': entitiesOfKind(state, 'artifact').length,
      // All of them. There is no active/inactive flag to filter on any more: whether
      // a client is live is told by their deals and when you last spoke.
      '/orgs': entitiesOfKind(state, 'org').length,
      '/people': entitiesOfKind(state, 'person').length,
      '/due': overdue,
      '/staging': pending,
    } as Record<string, number>
  })()

  // Which tab to light when the router cannot work it out on its own. This covers
  // `/c/:orgId` and `/b/:workId` as well as `/e/:id`: both used to light nothing,
  // which is what made drilling in feel like leaving the app.
  const activeTab = placeOf(pathname, (id) => state.entities[id]?.kind)

  return (
    <div className="flex h-full flex-col">
      {demo ? <DemoBar contact={DEMO_CONTACT} /> : null}
      {/* The device edges are owned in global.css (`@media (display-mode: standalone)`),
          not here. They used to be nobody's: the comment that stood here said the
          General Text shell owned them, which was true when crum was an app inside it
          and wrong the moment it shipped standalone. */}
      {/* **Deliberately unpositioned, and that is load-bearing on iOS.**
          `relative z-50` here was the obvious fix for a dropdown being painted over
          by the page's own `sticky top-0 z-30` bar inside <main>. It also made the
          header a compositing layer — a positioned, z-indexed element has to be
          promoted to keep paint order against the composited scroller beside it —
          and in an installed app on iOS that layer was rasterised at the wrong
          scale, so the whole header rendered permanently soft while everything in
          the scroller stayed crisp. Verified on the device: of eight candidate
          fixes, `translateZ(0)` and `isolation` changed nothing, and only moving
          the header inside the scroller (a structural re-rasterisation) cleared it.
          So the header stays out of the compositor, and the menus win the z-order
          on their own at z-40 — see WorkspaceSwitcher / WorkspaceMenu. */}
      <header
        className="shrink-0 border-b"
        style={{ borderColor: 'var(--border)', background: 'var(--rail)' }}
      >
        <div className="flex items-center gap-3 px-3 pt-2 sm:px-4">
          <WorkspaceSwitcher />

          <button
            type="button"
            onClick={() => setCmdOpen(true)}
            className="rounded-control text-meta flex min-w-0 flex-1 items-center gap-2 border px-2.5 py-1 sm:max-w-[280px]"
            style={{
              borderColor: 'color-mix(in srgb, var(--fg) 12%, transparent)',
              color: 'var(--muted)',
              background: 'color-mix(in srgb, var(--panel) 55%, transparent)',
            }}
          >
            <Icon name="Search" size={14} />
            <span className="truncate">Jump to anything</span>
            <kbd
              className="rounded-inline ml-auto hidden px-1.5 sm:inline"
              style={{
                background: 'var(--rail-hover)',
                fontFamily: 'var(--mono)',
                fontSize: 'var(--text-micro)',
              }}
            >
              ⌘K
            </kbd>
          </button>

          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            <WorkspaceMenu />
            <ThemeToggle />
            <NavLink
              to="/settings"
              className="rounded-control flex h-7 w-7 items-center justify-center hover:bg-[var(--rail-hover)]"
              style={({ isActive }) => ({ color: isActive ? 'var(--fg)' : 'var(--muted)' })}
              title="Settings"
              aria-label="Settings"
            >
              <Icon name="Settings" size={16} />
            </NavLink>
          </span>
        </div>

        {/* The tab strip scrolls sideways rather than folding into a "more" menu:
            nine items is a strip you can see, not a hierarchy worth hiding.

            One rule splits it: the five tabs that name a record kind, then the four
            surfaces you visit on purpose. They were nine identical items, which is
            what made the app read as nine equal rooms. Nothing moves route. */}
        <nav className={STRIP_CLASS} role="tablist">
          {TABS.map((tab, i) => (
            <Fragment key={tab.to}>
              {tab.group === 'surface' && TABS[i - 1]?.group === 'record' && (
                <span
                  aria-hidden
                  className="my-1.5 w-px shrink-0"
                  style={{ background: 'var(--border)' }}
                />
              )}
              <ViewTab tab={tab} count={counts[tab.to]} forced={activeTab === tab.to} />
            </Fragment>
          ))}
        </nav>
      </header>

      <div className="flex min-h-0 flex-1">
        <main ref={scroller} className="ground min-w-0 flex-1 overflow-auto">
          <Outlet />
        </main>
        {peekId && (
          <Peek
            id={peekId}
            onClose={closePeek}
            // Clicking a linked record inside the panel RE-POINTS the panel instead of
            // navigating away. Following a chain of relationships is the main thing
            // anyone does in here, and leaving the sheet to do it defeats the panel.
            onSelect={(next) => {
              const params2 = new URLSearchParams(params)
              params2.set('peek', next)
              setParams(params2, { replace: true })
            }}
          />
        )}
      </div>

      <CommandBar open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </div>
  )
}

/** One tab: the label, its count, and a 2px accent underline when it's the one. */
function ViewTab({
  tab,
  count,
  forced,
}: {
  tab: Tab
  count?: number | undefined
  forced?: boolean
}) {
  const hue = hueVar(tab)
  return (
    <NavLink
      to={tab.to}
      className={TAB_CLASS}
      style={({ isActive }) => tabStyle(tab, isActive || forced === true)}
    >
      {({ isActive }) => (
        <>
          <Icon
            name={tab.icon}
            size={14}
            className="sm:hidden"
            style={{ color: isActive || forced ? hue : 'var(--faint)' }}
          />
          {tab.label}
          {count ? (
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 'var(--text-micro)',
                color: 'var(--faint)',
              }}
            >
              {count}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  )
}

/**
 * Light or dark, and nothing else.
 *
 * No "system" option: it is a third state that reads as a preference but behaves
 * like an absence, and the mode it resolves to is the one setting nobody can see
 * or point at. Two states, one control, and the icon shows what you would get by
 * pressing it rather than what you currently have.
 */
function ThemeToggle() {
  const [mode, setMode] = useState<Mode>(() => themeMode())
  const next = mode === 'dark' ? 'light' : 'dark'
  return (
    <button
      type="button"
      onClick={() => setMode(toggleTheme())}
      className="rounded-control flex h-7 w-7 items-center justify-center hover:bg-[var(--rail-hover)]"
      style={{ color: 'var(--muted)' }}
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
    >
      <Icon name={mode === 'dark' ? 'Sun' : 'Moon'} size={16} />
    </button>
  )
}
